import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    cleanupScenarioData,
    ScenarioCleanupAction,
    ScenarioCleanupReport,
} from '../../api/scenarioCleanup';
import { suiteFailureMessage } from '../../api/scenarioSuites';
import { useAxiosSwagger } from '../../hooks/useAxiosSwagger';

type Step = 'idle' | 'looking' | 'preview' | 'deleting' | 'done' | 'error';

const SYSTEMS = ['Exact', 'Connector', 'Chargebee'];

/** "3 in Exact · 1 in Connector · 7 in Chargebee", counting only the actions with the given outcome. */
const perSystem = (actions: ScenarioCleanupAction[], outcome: string) =>
    SYSTEMS.map((system) => ({
        system,
        count: actions.filter(
            (action) => action.system === system && action.outcome === outcome
        ).length,
    }))
        .filter((entry) => entry.count > 0)
        .map((entry) => `${entry.count} in ${entry.system}`)
        .join(' · ');

const ActionList = ({ actions }: { actions: ScenarioCleanupAction[] }) => (
    <ul className="arl-cleanup__list">
        {actions.map((action, index) => (
            <li
                key={index}
                className={`arl-cleanup__action arl-cleanup__action--${action.outcome}`}
            >
                <span className="arl-cleanup__system">{action.system}</span>
                <span className="arl-cleanup__kind">{action.kind}</span>
                <code>{action.id}</code>
                {action.detail && (
                    <span className="arl-cleanup__detail">{action.detail}</span>
                )}
            </li>
        ))}
    </ul>
);

/**
 * Removes what scenario runs created in Chargebee and Exact, in two steps: first what would go, then the delete.
 *
 * Without a run id it covers every scenario customer of the environment. Only customers whose id starts with
 * scn- are ever touched; the API refuses while a run is going.
 */
export const ScenarioCleanup = ({
    customer,
    environment,
    runId,
    disabled,
    disabledReason,
}: {
    customer: string;
    environment: string;
    runId?: string;
    disabled?: boolean;
    disabledReason?: string;
}) => {
    const axios = useAxiosSwagger();
    const [step, setStep] = useState<Step>('idle');
    const [report, setReport] = useState<ScenarioCleanupReport | null>(null);
    const [error, setError] = useState<string>('');

    const scope = runId ? 'this run' : 'all scenario runs';

    const call = async (apply: boolean) => {
        if (!axios) return;

        setStep(apply ? 'deleting' : 'looking');

        try {
            setReport(
                await cleanupScenarioData(axios, customer, environment, runId, apply)
            );
            setStep(apply ? 'done' : 'preview');
        } catch (failure) {
            setError(
                suiteFailureMessage(failure, 'The test data could not be cleaned up.')
            );
            setStep('error');
        }
    };

    const close = () => {
        setStep('idle');
        setReport(null);
        setError('');
    };

    if (step === 'idle') {
        return (
            <button
                type="button"
                className="btn btn-sm btn-outline-secondary arl-cleanup__open"
                disabled={disabled}
                title={disabled ? disabledReason : `Show what ${scope} left in Chargebee and Exact, then delete it`}
                onClick={() => call(false)}
            >
                <FontAwesomeIcon icon="broom" className="me-2" />
                Clean up test data
            </button>
        );
    }

    const actions = report?.actions ?? [];
    const failed = actions.filter((action) => action.outcome === 'failed');
    const leftBehind = actions.filter((action) => action.outcome === 'leftBehind');
    const deleted = actions.filter((action) => action.outcome === 'deleted');

    return (
        <div className="arl-cleanup" role="region" aria-label="Clean up test data">
            {step === 'looking' && (
                <p className="arl-cleanup__line">
                    <FontAwesomeIcon icon="spinner" spin /> Looking up what {scope} left behind…
                </p>
            )}

            {step === 'deleting' && (
                <p className="arl-cleanup__line">
                    <FontAwesomeIcon icon="spinner" spin /> Deleting {actions.length} objects…
                </p>
            )}

            {step === 'error' && (
                <>
                    <p className="arl-cleanup__line arl-cleanup__line--error">{error}</p>
                    <div className="arl-cleanup__buttons">
                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={close}>
                            Close
                        </button>
                    </div>
                </>
            )}

            {step === 'preview' && report && (
                <>
                    {report.customerCount === 0 ? (
                        <p className="arl-cleanup__line">
                            Nothing to clean up: {scope} left no test data in {report.target}.
                        </p>
                    ) : (
                        <>
                            <p className="arl-cleanup__line">
                                <strong>
                                    {actions.length} objects of {report.customerCount} test customer(s) will be deleted
                                </strong>
                                : {perSystem(actions, 'wouldDelete')}.
                            </p>
                            <p className="arl-cleanup__hint">
                                Reaches {report.target}. Only customers whose id starts with <code>scn-</code>; item
                                prices and coupons stay.
                            </p>
                            <details className="arl-cleanup__details">
                                <summary>Show every object</summary>
                                <ActionList actions={actions} />
                            </details>
                        </>
                    )}
                    <div className="arl-cleanup__buttons">
                        {(report.customerCount ?? 0) > 0 && (
                            <button type="button" className="btn btn-sm btn-danger" onClick={() => call(true)}>
                                Delete {actions.length} objects
                            </button>
                        )}
                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={close}>
                            {(report.customerCount ?? 0) > 0 ? 'Cancel' : 'Close'}
                        </button>
                    </div>
                </>
            )}

            {step === 'done' && report && (
                <>
                    <p className={`arl-cleanup__line${failed.length || leftBehind.length ? ' arl-cleanup__line--warn' : ' arl-cleanup__line--ok'}`}>
                        <strong>Deleted {deleted.length} objects</strong>
                        {deleted.length > 0 && `: ${perSystem(actions, 'deleted')}`}.
                        {failed.length > 0 && ` ${failed.length} could not be deleted.`}
                        {leftBehind.length > 0 && ` ${leftBehind.length} need a person in Exact.`}
                    </p>
                    {failed.length + leftBehind.length > 0 && (
                        <ActionList actions={[...failed, ...leftBehind]} />
                    )}
                    <div className="arl-cleanup__buttons">
                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={close}>
                            Close
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};
